import type { PowerBiReport } from "../api/powerBiReportsApi.js";

/** Pumpstation's machine-readable cause of a 422, sent as the error's first parameter. */
export const POWER_BI_SETUP_REASONS = [
	"consent_missing",
	"service_principals_disabled",
	"no_workspaces",
] as const;

export type PowerBiSetupReason = (typeof POWER_BI_SETUP_REASONS)[number];

export type PowerBiSetupStatus =
	| { kind: "loading" }
	/** No connection for the company yet. */
	| { kind: "notConnected" }
	/** Connected, but Power BI doesn't give the app access yet; reason is null if Pumpstation sent none we know. */
	| { kind: "setupIncomplete"; reason: PowerBiSetupReason | null }
	| { kind: "error" }
	| { kind: "ready"; reports: PowerBiReport[] };

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

const errorStatus = (error: unknown) =>
	isRecord(error) ? error.status : undefined;

export const readSetupReason = (error: unknown): PowerBiSetupReason | null => {
	if (!isRecord(error) || !isRecord(error.data)) return null;
	const [reason] = Array.isArray(error.data.parameters)
		? error.data.parameters
		: [];
	return POWER_BI_SETUP_REASONS.find((known) => known === reason) ?? null;
};

/** Maps the reports query to where the company is in the Power BI setup. */
export const toSetupStatus = ({
	reports,
	isLoading,
	error,
}: {
	reports: PowerBiReport[] | undefined;
	isLoading: boolean;
	error: unknown;
}): PowerBiSetupStatus => {
	if (isLoading) return { kind: "loading" };
	// WHY by status: Pumpstation answers 404 for a company without a connection and 422 when the customer-side setup is incomplete.
	if (errorStatus(error) === 404) return { kind: "notConnected" };
	if (errorStatus(error) === 422)
		return { kind: "setupIncomplete", reason: readSetupReason(error) };
	if (error || !reports) return { kind: "error" };
	return { kind: "ready", reports };
};

export type PowerBiSetupStep = 0 | 1 | 2;

/** The wizard step the company is stuck at: approve the app, allow service principals, or add the app to workspaces. */
export const setupStepFor = (
	status: Extract<
		PowerBiSetupStatus,
		{ kind: "notConnected" | "setupIncomplete" }
	>,
): PowerBiSetupStep => {
	if (status.kind === "notConnected") return 0;
	switch (status.reason) {
		case "consent_missing":
			return 0;
		case "no_workspaces":
			return 2;
		default:
			// WHY step 1 for an unknown reason: right after approval nothing else is set up yet.
			return 1;
	}
};
